import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Heading, Paragraph } from '@digdir/designsystemet-react';
import type { HrWorkEnvironmentOverview } from '../../../../api/hrWorkEnvironment';
import { recurringRisk, riskShares } from '../Overview/hrOverviewView';
import { OverviewSubpage } from '../Overview/OverviewSubpage';
import { RiskBar } from '../Overview/RiskBar';
import classes from '../Overview/Overview.module.css';

const LEGEND = ['high', 'moderate'] as const;

const Risks = ({
  overview,
}: {
  overview: HrWorkEnvironmentOverview;
}): ReactElement => {
  const { t } = useTranslation();
  const insight = recurringRisk(overview);
  if (overview.risks.length === 0)
    return (
      <Paragraph>
        {t('hrOverview.risks.none', { months: overview.setup.intervalMonths })}
      </Paragraph>
    );
  return (
    <>
      {insight && (
        <Alert data-color="info">
          <Heading level={2} data-size="2xs" className={classes.alertHeading}>
            {t('hrOverview.risks.insightTitle', {
              factor: insight.name,
              ...insight,
            })}
          </Heading>
          <Paragraph data-size="sm">
            {t('hrOverview.risks.insightText')}
          </Paragraph>
        </Alert>
      )}
      <section aria-labelledby="factors-heading" className={classes.card}>
        <Heading level={2} data-size="xs" id="factors-heading">
          {t('hrOverview.risks.title')}
        </Heading>
        <ul className={classes.legend}>
          {LEGEND.map((level) => (
            <li key={level}>
              <span
                aria-hidden="true"
                className={`${classes.dot} ${classes.level}`}
                data-level={level}
              />
              <span className="ds-paragraph" data-size="sm">
                {t(`hrOverview.risks.${level}`)}
              </span>
            </li>
          ))}
        </ul>
        <ul className={classes.rows}>
          {overview.risks.map((risk) => (
            <li key={risk.key} className={classes.riskRow}>
              <span className={classes.rowText}>
                <span className="ds-paragraph" data-size="sm">
                  {risk.name}
                </span>
                {risk.sensitive && (
                  <span
                    className={`ds-paragraph ${classes.subtle}`}
                    data-size="xs"
                  >
                    {t('hrOverview.risks.shielded')}
                  </span>
                )}
              </span>
              <RiskBar shares={riskShares(risk, overview)} />
              <span className={`ds-paragraph ${classes.nowrap}`} data-size="sm">
                {t('hrOverview.risks.count', risk)}
              </span>
            </li>
          ))}
        </ul>
        <Paragraph data-size="sm" className={classes.subtle}>
          {t('hrOverview.risks.anonymous')}
        </Paragraph>
      </section>
    </>
  );
};

export const HRRisks = (): ReactElement => (
  <OverviewSubpage
    page="risks"
    values={(overview) => ({
      count: overview.teamsWithResults,
      months: overview.setup.intervalMonths,
    })}
  >
    {(overview) => <Risks overview={overview} />}
  </OverviewSubpage>
);
